type BooleanLike = string | boolean | null | undefined;

const sanitizeString = (value: string | undefined): string | undefined => {
  if (!value) {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed || trimmed.toLowerCase() === 'undefined' || trimmed.toLowerCase() === 'null') {
    return undefined;
  }

  return trimmed;
};

const parseBoolean = (value: BooleanLike): boolean | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }

  const normalized = String(value).trim().toLowerCase();
  if (['1', 'true', 'on', 'enabled', 'yes'].includes(normalized)) {
    return true;
  }
  if (['0', 'false', 'off', 'disabled', 'no'].includes(normalized)) {
    return false;
  }
  return undefined;
};

const readFromProcessEnv = (key: string): string | undefined => {
  if (typeof process === 'undefined' || typeof process.env === 'undefined') {
    return undefined;
  }

  return process.env[key];
};

const getImportMetaEnv = (): Record<string, unknown> | undefined => {
  try {
    // Avoid direct syntax usage of import.meta for Jest/CommonJS compatibility
    // eslint-disable-next-line no-new-func
    const importMeta = Function('return typeof import !== "undefined" ? import.meta : undefined;')();
    if (!importMeta || typeof importMeta !== 'object') {
      return undefined;
    }
    const env = (importMeta as { env?: Record<string, unknown> }).env;
    if (!env || typeof env !== 'object') {
      return undefined;
    }
    return env;
  } catch {
    return undefined;
  }
};

const readFromImportMeta = (key: string): string | undefined => {
  const env = getImportMetaEnv();
  if (!env) {
    return undefined;
  }

  const value = env[key];
  if (typeof value === 'string') {
    return value;
  }
  if (value === undefined || value === null) {
    return undefined;
  }
  return String(value);
};

const readEnvValue = (keys: string[]): string | undefined => {
  for (const key of keys) {
    const rawValue = sanitizeString(readFromProcessEnv(key) ?? readFromImportMeta(key));
    if (rawValue !== undefined) {
      return rawValue;
    }
  }

  return undefined;
};

const measurementId = readEnvValue(['REACT_APP_GA_MEASUREMENT_ID', 'VITE_GA_MEASUREMENT_ID']);
const debugViewFlag = parseBoolean(readEnvValue(['REACT_APP_GA_DEBUG_VIEW', 'VITE_GA_DEBUG_VIEW']));
const enabledFlag = parseBoolean(
  readEnvValue([
    'REACT_APP_ENABLE_ANALYTICS',
    'REACT_APP_ANALYTICS_ENABLED',
    'VITE_ENABLE_ANALYTICS',
    'VITE_ANALYTICS_ENABLED'
  ])
);
const requireConsentFlag = parseBoolean(
  readEnvValue(['REACT_APP_REQUIRE_ANALYTICS_CONSENT', 'VITE_REQUIRE_ANALYTICS_CONSENT'])
);
const defaultConsentFlag = parseBoolean(
  readEnvValue(['REACT_APP_ANALYTICS_DEFAULT_CONSENT', 'VITE_ANALYTICS_DEFAULT_CONSENT'])
);
const consentStorageKey =
  readEnvValue(['REACT_APP_ANALYTICS_CONSENT_STORAGE_KEY', 'VITE_ANALYTICS_CONSENT_STORAGE_KEY']) ??
  'eruption.analytics-consent';

const enabled = enabledFlag ?? false;
const requireConsent = requireConsentFlag ?? false;
const defaultConsent = defaultConsentFlag ?? !requireConsent;

export const analyticsEnv = Object.freeze({
  measurementId,
  debugView: debugViewFlag ?? false,
  enabled,
  requireConsent,
  defaultConsent,
  consentStorageKey,
  shouldAutoEnable: enabled && !!measurementId && (!requireConsent || defaultConsent)
});

export type AnalyticsEnvironment = typeof analyticsEnv;
